import { QuotingPortal } from 'lib/hosts/quoting/interface';
import { Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

export const TruckerToolsSubmitAction = 'truckertools-submit-quote';

const TRUCKER_TOOLS_HOST = 'truckertools.com';

export function isTruckerToolsHost(hostname: Undef<string>): boolean {
  if (!hostname) {
    return false;
  }

  const normalized = hostname.toLowerCase();
  return (
    normalized === TRUCKER_TOOLS_HOST ||
    normalized.endsWith(`.${TRUCKER_TOOLS_HOST}`)
  );
}

export function isTruckerToolsQuotePath(pathname: Undef<string>): boolean {
  if (!pathname) {
    return false;
  }

  const path = pathname.toLowerCase().replace(/\/+$/, '');
  return path === '/loads';
}

export class TruckerTools implements QuotingPortal {
  submitAction = TruckerToolsSubmitAction;

  // This does not gate the SUBMIT OFFER click; the background handler does.
  // This function answers: "is the bid form open, or can it be opened via a click?"
  canSubmit(tab: Undef<chrome.tabs.Tab>, html: string): boolean {
    try {
      const url = tab?.url ? new URL(tab.url) : null;
      if (!url || !isTruckerToolsHost(url.hostname)) {
        return false;
      }
      if (!isTruckerToolsQuotePath(url.pathname)) {
        return false;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const buttons = Array.from(doc.querySelectorAll('button'));

      const hasQuoteInput = doc.querySelector('[data-cy="currency-input"]');
      const hasQuoteButton = buttons.some(
        (button) => button.textContent?.trim().toLowerCase() === 'quote'
      );
      const hasSubmitOffer = buttons.some((button) =>
        /submit\s+offer/i.test(button.textContent ?? '')
      );

      return Boolean(hasQuoteInput || hasQuoteButton || hasSubmitOffer);
    } catch (error: unknown) {
      captureException(error, {
        context: 'Error checking whether Trucker Tools can submit a quote',
      });
      return false;
    }
  }
}
